import { useEffect, useRef, useState } from 'react';
import css from './index.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBeforeUnloadAndNavigate, useScrollToTop } from '@helpers/hooks';
import { Loader, Typography } from '@components/base';
import { SideBar } from '@components/common';
import { CLIENT_ROUTES } from '@router/routes';
import { useNavigate, useParams } from 'react-router-dom';
import ShipmentDetails from './components/shipment-details';
import InstructionAndCharges from './components/instructions-and-charges';
import { fetchOrderInfo, generateBillOfLading } from '@services/order.service';
import { fetchSupplierWarehouses } from '@services/supplier.service';
import notify from '@helpers/toastify-helper';
import {
  instructionAndChargesValidationSchema,
  shipmentDetailsValidationSchema
} from '@helpers/yup/generate-bill-of-lading';
import BillOfLadingPreview from './components/preview';
import { fetchCustomerDetail } from '@services/customer.service';
import {
  commodityInfo,
  customerOrderInformation,
  IBillOfLadingState,
  InstructionAndChargesFormValues,
  ShipmentDetailsFormValues
} from './bol';

const extractCustomerOrderInfo = (orderData: any): customerOrderInformation => {
  let totalPackages = 0;
  let totalWeight = 0;
  const unitOfWeight = orderData?.unit_of_weight || 'KG';

  if (orderData?.order_item && Array.isArray(orderData.order_item)) {
    orderData.order_item.forEach((orderItem: any) => {
      if (orderItem.package && Array.isArray(orderItem.package)) {
        orderItem.package.forEach((pkg: any) => {
          totalPackages += pkg.no_of_package || 0;
          totalWeight += pkg.weight * pkg.no_of_package || 0;
        });
      }
    });
  }

  return {
    customerPoNumber: orderData?.po_reference_no || '',
    totalPackages: totalPackages,
    totalWeight: totalWeight.toString(),
    unitOfWeight: unitOfWeight
  };
};

const extractCommodityInfo = (orderData: any): commodityInfo[] => {
  const commodityInfo: commodityInfo[] = [];

  if (orderData?.order_item && Array.isArray(orderData.order_item)) {
    orderData.order_item.forEach((orderItem: any) => {
      if (orderItem.package && Array.isArray(orderItem.package)) {
        orderItem.package.forEach((pkg: any) => {
          commodityInfo.push({
            productName: pkg.order_item_name || '',
            packageCount: pkg.no_of_package || 0,
            packageDescription: pkg.display_name_with_no_of_package || '',
            netWeight: `${pkg.weight * pkg.no_of_package} ${pkg.unit_of_weight || ''}`
          });
        });
      }
    });
  }

  return commodityInfo;
};

const BillOfLading = () => {
  const topRef = useRef(null);
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params?.orderid;
  const taskId = params?.taskid;

  const [billOfLading, setBillOfLading] = useState<IBillOfLadingState>({
    isLoading: false,
    activeStep: 0,
    orderInfo: null,
    warehouseOptions: [],
    customerOrderInformation: null,
    commodityInformation: null,
    customerPocOptions: []
  });

  const { isLoading, activeStep, customerOrderInformation, commodityInformation } = billOfLading;

  const getWarehouseDetails = async () => {
    setBillOfLading((prevState: IBillOfLadingState) => ({
      ...prevState,
      isLoading: true
    }));
    const response = await fetchSupplierWarehouses();
    if (response.success) {
      setBillOfLading((prevState: IBillOfLadingState) => ({
        ...prevState,
        isLoading: true,
        warehouseOptions: response.data?.results || []
      }));
    } else if (response.error) {
      setBillOfLading((prevState: IBillOfLadingState) => ({
        ...prevState,
        isLoading: false
      }));
      notify({
        severity: 'error',
        message: response.error ?? 'Something Went Wrong, Contact Tech Team'
      });
    }
  };

  const getCustomerDetails = async (customer_id: string) => {
    const response = await fetchCustomerDetail(customer_id);
    if (response.success) {
      const { data } = response;
      setBillOfLading((prevState: IBillOfLadingState) => ({
        ...prevState,
        customerPocOptions: data.pocs
      }));
    } else {
      notify({
        severity: 'error',
        message: response.error ?? 'Something Went Wrong, Contact Tech Team'
      });
    }
  };

  const shipmentDetailsForm = useForm<ShipmentDetailsFormValues>({
    resolver: yupResolver(shipmentDetailsValidationSchema),
    defaultValues: {
      warehouseDetails: null,
      deliveryLocation: {
        companyName: '',
        address: '',
        email: null
      },
      identifiers: {
        invoiceNumber: '',
        quoteNumber: ''
      },
      additionalNotes: ''
    }
  });

  const { setValue } = shipmentDetailsForm;

  const createShipmentForm = useForm<InstructionAndChargesFormValues>({
    resolver: yupResolver(instructionAndChargesValidationSchema),
    defaultValues: {
      carrierInfo: {
        name: '',
        quoteId: ''
      },
      instructions: {
        emergencyContact: '',
        originInstructions: '',
        destinationInstructions: '',
        originAccessorials: '',
        destinationAccessorials: '',
        special: ''
      },
      freightCharges: {
        prepaid: null,
        prepaidCurrency: null,
        collect: null,
        collectCurrency: null,
        thirdParty: null,
        thirdPartyCurrency: null
      }
    }
  });

  const getOrderInfo = async (orderId: string) => {
    if (orderId) {
      setBillOfLading((prevState: IBillOfLadingState) => ({ ...prevState, isLoading: true }));
      const response = await fetchOrderInfo(orderId);
      if (response.success) {
        const { data } = response;

        setValue('deliveryLocation', {
          companyName: data?.consignee_name,
          address: data?.consignee_office_address,
          email: data?.customer_poc
        });

        setValue('identifiers.invoiceNumber', data?.order_pi?.document_number);

        const customerId = data.customer.customer_id;
        const commodityInfo = extractCommodityInfo(data);
        const customerOrderInfo = extractCustomerOrderInfo(data);

        getCustomerDetails(customerId);

        setBillOfLading((prevState: IBillOfLadingState) => ({
          ...prevState,
          orderInfo: data,
          commodityInformation: commodityInfo,
          customerOrderInformation: customerOrderInfo,
          isLoading: false
        }));
      } else {
        setBillOfLading((prevState: IBillOfLadingState) => ({ ...prevState, isLoading: false }));
        navigate('/404');
      }
    }
  };

  useScrollToTop({ topRef, dependencyArray: [activeStep] });

  const handleCancelClick = () => {
    navigate(`/${CLIENT_ROUTES.order}/${orderId}`);
  };

  const handleNavigation = (currentStep: number) => {
    setBillOfLading((prevState: IBillOfLadingState) => ({
      ...prevState,
      activeStep: currentStep
    }));
  };

  const handleSidebarClick = (value: number) => {
    handleNavigation(value);
  };

  const handleSidebarEnterKey = (value: number) => {
    handleNavigation(value);
  };

  const constructReqBody = () => {
    const firstFormData = shipmentDetailsForm.getValues();
    const secondFormData = createShipmentForm.getValues();

    const reqBody = {
      warehouse: firstFormData.warehouseDetails?.warehouse_id ?? null,
      order: orderId,
      user: firstFormData.deliveryLocation.email?.user_id ?? null,
      address: firstFormData.deliveryLocation.address,
      quote_number: firstFormData.identifiers.quoteNumber,
      carrier_name: secondFormData.carrierInfo.name,
      carrier_quote_id: secondFormData.carrierInfo.quoteId,
      emergency_contact_number: secondFormData.instructions.emergencyContact,
      origin_instructions: secondFormData.instructions.originInstructions,
      destination_instructions: secondFormData.instructions.destinationInstructions,
      origin_accessorials: secondFormData.instructions.originAccessorials,
      destination_accessorials: secondFormData.instructions.destinationAccessorials,
      freight_prepaid_currency: secondFormData.freightCharges.prepaidCurrency?.value ?? null,
      freight_prepaid_amount: secondFormData.freightCharges.prepaid,
      freight_collect_currency: secondFormData.freightCharges.collectCurrency?.value ?? null,
      freight_collect_amount: secondFormData.freightCharges.collect,
      freight_third_party_currency: secondFormData.freightCharges.thirdPartyCurrency?.value ?? null,
      freight_third_party_amount: secondFormData.freightCharges.thirdParty,
      additional_notes: firstFormData.additionalNotes,
      customer_po_number: customerOrderInformation?.customerPoNumber || '',
      special_instructions: secondFormData.instructions.special
    };

    return reqBody;
  };

  useEffect(() => {
    getOrderInfo(orderId ?? '');
  }, [orderId]);

  useEffect(() => {
    getWarehouseDetails();
  }, []);

  if (isLoading) {
    return <Loader open={isLoading} />;
  }

  const onFormSubmit = async () => {
    const reqBody = constructReqBody();
    const response = await generateBillOfLading(taskId ?? '', reqBody);
    if (response.success) {
      notify({
        message: 'Bill of Lading, Created successfully',
        severity: 'success',
        dismissible: true
      });
      navigate(`/${CLIENT_ROUTES.order}/${orderId as string}`);
    } else {
      notify({
        severity: 'error',
        message: response.error.message || 'Something Went Wrong'
      });
    }
  };

  return (
    <main className={css.mainWrapper}>
      <section className={css.mainHeader}>
        <Typography variant="h2">Generate Bill of Lading</Typography>
        <Typography variant="subheading1">0{activeStep + 1} of 03</Typography>
      </section>
      <section className={css.mainContent} ref={topRef}>
        <div className={css.sidebarWrapper}>
          <SideBar
            activeStep={activeStep}
            onClick={handleSidebarClick}
            onEnter={handleSidebarEnterKey}
          >
            <SideBar.Item label="Shipment Details" value={0} />
            <SideBar.Item label="Instructions and Charges" value={1} disabled={activeStep! <= 1} />
            <SideBar.Item label="Preview" value={4} disabled={activeStep! <= 2} />
          </SideBar>
        </div>
        {activeStep === 0 && (
          <FormProvider {...shipmentDetailsForm}>
            <ShipmentDetails
              handleShipmentDetailsSubmit={() => handleNavigation(1)}
              handleCancelClick={handleCancelClick}
              data={billOfLading}
            />
          </FormProvider>
        )}
        {activeStep === 1 && (
          <FormProvider {...createShipmentForm}>
            <InstructionAndCharges
              handleInstructionAndCharges={() => handleNavigation(2)}
              handleCancelClick={handleCancelClick}
            />
          </FormProvider>
        )}
        {activeStep === 2 && (
          <BillOfLadingPreview
            firstForm={shipmentDetailsForm.getValues()}
            secondForm={createShipmentForm.getValues()}
            customerOrderInformation={customerOrderInformation}
            commodityInformation={commodityInformation}
            handleCancelClick={handleCancelClick}
            onFormSubmit={onFormSubmit}
          />
        )}
      </section>
    </main>
  );
};

export default BillOfLading;
